import {
  ExperienceCardFacets,
  ExperienceCardFooter,
} from '@/components/admin/experiences/experience-card';
import {
  ExperienceLinkFilterField,
  ExperienceReferenceFilters,
} from '@/components/admin/experiences/experience-filter-fields';
import { FilterBar } from '@/components/admin/versioned/filter-bar';
import { FilterPanel } from '@/components/admin/versioned/filter-panel';
import { FilterSummary } from '@/components/admin/versioned/filter-summary';
import { VersionedGrid } from '@/components/admin/versioned/versioned-grid';
import { Callout } from '@/components/ui/callout';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { duplicateExperience, fetchExperiences, restoreExperience } from '@/lib/api';
import {
  activeExperienceFilters,
  experienceAuthors,
  experienceCounts,
  experienceFiltersOf,
  experienceReferenceOptions,
  experienceSearchOf,
  filterExperiences,
  hasActiveExperienceFilters,
  hasHiddenExperienceFilter,
  readExperienceFilters,
  resetExperienceFilters,
} from '@/lib/experience-filters';
import { EXPERIENCE_WORDING } from '@/lib/experience-wording';
import { useVersionedDuplication } from '@/lib/versioned-duplication';
import { VERSIONED_SORT_LABEL, versionedRefusal, versionedRefusalOf } from '@/lib/versioned-labels';
import type { ExperienceCard, ExperienceFilters } from '@matter_extractor/shared';
import { experienceSortSchema } from '@matter_extractor/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * La bibliothèque d'expériences - the screen that replaces the couture of #32
 * (refonte modulaire §4, §9, §15; maquette `Main.dc.html`; issue #55).
 *
 * The fourth library, and the third to add filters of its own. What is left for
 * this file is the wording, the two card blocks of §15 - the line of facets and
 * the foot - and the three own filters, two of which take the place of the
 * layer's referent selects (see `experience-filter-fields.tsx`).
 *
 * ## The whole library in one read, filtered in the page
 *
 * `GET /api/experiences` serves every experience, retired ones included, and
 * the filters of §4 are applied by `lib/versioned-filters.ts`. That decision
 * belongs to `experienceListSchema` and is argued there; what it buys this
 * screen is that a keystroke in « Nom » costs a `filter()` rather than a round
 * trip - and that the two reference selects can offer the names the library
 * actually holds.
 *
 * ## The filters live in the URL
 *
 * The régime of the three libraries before it: a filtered list is exactly what
 * somebody pastes into a team thread, and a reload must not throw away what an
 * operator typed. `validateSearch` NEVER throws - a typo in a pasted link
 * degrades to the neutral filter rather than to a blank screen.
 *
 * ## One cache key, one shape (issue #16)
 *
 * `['experiences', email]` holds an `ExperienceList` and nothing else. The
 * editor reads `['experience', email, id]`, which is a different shape under a
 * different prefix - the trap of #16 is two components agreeing on a key and
 * disagreeing on what is under it, and `useQuery` is generic per component so
 * nothing would catch it.
 */

export const Route = createFileRoute('/admin/experiences/')({
  validateSearch: readExperienceFilters,
  component: ExperiencesPage,
});

const INTRO =
  'Une expérience est une timeline de pages autour d’une page protocole : chaque page est un flux vertical de blocs, avec ses temps d’apparition et son thème. Elle se versionne comme un critère, un persona ou un protocole, et se gèle au démarrage d’une session.';

function ExperiencesPage() {
  const { email } = useAdminSession();
  const filters = experienceFiltersOf(Route.useSearch());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Is the « Plus de filtres » panel open? Component state and not a search
   * param, the régime the three libraries before it settled: it is a posture of
   * the reader and not a property of the list. It opens on its own when a link
   * arrives with one of the hidden filters armed.
   */
  const [expanded, setExpanded] = useState(() => hasHiddenExperienceFilter(filters));

  const experiences = useQuery({
    queryKey: ['experiences', email],
    queryFn: fetchExperiences,
    retry: false,
  });

  const rejected = useRejectOn401(experiences.error);

  const restore = useMutation({
    mutationFn: (card: ExperienceCard) => restoreExperience(card.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['experiences', email] }),
  });

  /**
   * « Dupliquer », from the card's « … » menu (§4.2, issue #116).
   *
   * The mutation itself is the layer's (`lib/versioned-duplication.ts`): the
   * gesture is a route of the versioning FACTORY, and the four libraries call
   * it with the three things that differ - the API call, the list's cache key,
   * and the address of the copy's editor, which only this route can type
   * against the route tree.
   */
  const duplicate = useVersionedDuplication<ExperienceCard>({
    duplicate: duplicateExperience,
    listKey: 'experiences',
    onCopy: (experienceId) =>
      void navigate({
        to: '/admin/experiences/$experienceId',
        params: { experienceId },
        search: { copie: true },
      }),
  });

  /**
   * One dimension at a time, merged into the address bar - and REPLACED there,
   * never pushed: the « Nom » field filters as it is typed, so a push would
   * leave one history entry per keystroke.
   */
  const change = (patch: Partial<ExperienceFilters>) =>
    navigate({
      to: '/admin/experiences',
      search: experienceSearchOf({ ...filters, ...patch }),
      replace: true,
    });

  const cards = experiences.data?.experiences ?? [];
  const counts = experienceCounts(cards, filters);
  const chips = activeExperienceFilters(filters);
  const refused = versionedRefusalOf(restore.error);
  const duplicateRefused = versionedRefusalOf(duplicate.error);

  return (
    <section className="space-y-5 pb-16">
      <header className="space-y-1">
        {/*
          `/^expériences$/i` is what `admin-shell.spec.ts` and the door catalogue
          read: the couture carried this exact heading, and the real screen keeps
          it so those assertions never had a screen to chase.
        */}
        <h1 className="font-bold text-3xl text-bleu-nuit">Expériences</h1>
        <p className="max-w-[95ch] text-gray-600 text-sm">{INTRO}</p>
      </header>

      <FilterBar
        wording={EXPERIENCE_WORDING}
        filters={filters}
        authors={experienceAuthors(cards, filters)}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((was) => !was)}
        /*
          The two references an experience CARRIES, in the slots the layer keeps
          for the referents that carry IT (§15, maquette `Main.dc.html`). An
          experience's referents are the links of §5.1, so « Protocole : nulle
          part » and an « Expérience » select would both filter on a relation
          this screen cannot have - the argument is in `filter-bar.tsx`.
        */
        renderReferents={
          <ExperienceReferenceFilters
            filters={filters}
            options={experienceReferenceOptions(cards)}
            onChange={change}
          />
        }
        onChange={change}
      />

      {expanded ? (
        <FilterPanel
          wording={EXPERIENCE_WORDING}
          filters={filters}
          ownFilters={<ExperienceLinkFilterField filters={filters} onChange={change} />}
          retiredCount={counts.retired}
          onChange={change}
          onReset={() =>
            navigate({
              to: '/admin/experiences',
              search: experienceSearchOf(resetExperienceFilters(filters)),
              replace: true,
            })
          }
          resetDisabled={!hasActiveExperienceFilters(filters)}
        />
      ) : null}

      <FilterSummary
        wording={EXPERIENCE_WORDING}
        chips={chips}
        counts={counts}
        sort={filters.sort}
        sortOptions={experienceSortSchema.options}
        // The four common sorts and no more: an experience adds none (§15).
        sortLabel={VERSIONED_SORT_LABEL}
        onChange={change}
      />

      {duplicate.isError ? (
        <Callout tone="danger" live="alert" data-testid="experiences-duplication-refusee">
          {duplicateRefused === null
            ? 'La copie n’a pas pu être écrite. Réessayez dans un instant.'
            : versionedRefusal(duplicateRefused, EXPERIENCE_WORDING.refusal)}
        </Callout>
      ) : null}

      {restore.isError ? (
        <Callout tone="danger" live="alert" data-testid="experiences-restauration-refusee">
          {refused === null
            ? 'La restauration a échoué. Réessayez dans un instant.'
            : versionedRefusal(refused, EXPERIENCE_WORDING.refusal)}
        </Callout>
      ) : null}

      {experiences.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement des expériences" />
        </div>
      ) : null}

      {experiences.isError && !rejected ? (
        <p className="text-gray-700">La bibliothèque d’expériences n’a pas pu être chargée.</p>
      ) : null}

      {experiences.data ? (
        <VersionedGrid
          wording={EXPERIENCE_WORDING}
          cards={filterExperiences(cards, filters)}
          // Why it is empty, worded HERE: only this screen knows whether a
          // filter is armed.
          emptyMessage={
            chips.length === 0
              ? 'Aucune expérience pour l’instant. La carte en pointillé ouvre l’éditeur vide.'
              : 'Aucune expérience ne répond à ces filtres. Retirez une pastille, ou réinitialisez.'
          }
          /*
            NOTHING in the layer's line of facts: an experience's usage is
            counted in LINKS, which is not one of the four kinds
            `versionedUsageCount` knows, and its own facets sit ABOVE the
            pastilles (maquette `Main.dc.html`). Both ride the two slots below.
          */
          facetsOf={() => []}
          renderExtra={(card) => <ExperienceCardFacets card={card} />}
          renderFooter={(card) => <ExperienceCardFooter card={card} />}
          renderCardLink={(card, props) => (
            <Link
              to="/admin/experiences/$experienceId"
              params={{ experienceId: card.id }}
              className={props.className}
            >
              {props.children}
            </Link>
          )}
          renderCreateLink={(props) => (
            <Link
              to="/admin/experiences/nouveau"
              data-testid={props['data-testid']}
              className={props.className}
            >
              {props.children}
            </Link>
          )}
          onRestore={(card) => restore.mutate(card)}
          restoringId={restore.isPending ? (restore.variables?.id ?? null) : null}
          onDuplicate={(card, tag) => duplicate.mutate({ card, tag })}
          duplicatingId={duplicate.isPending ? (duplicate.variables?.card.id ?? null) : null}
        />
      ) : null}
    </section>
  );
}
